import { Climb } from '../climbing/Climb';
import { JsonProperty, JsonObject } from 'typescript-json-serializer';
import { ClimbPerformance } from '../climbing/Grade';

/**
 * Interface for gym membership with role
 */
export interface GymMembership {
  [gymId: string]: string; // gymId -> role mapping
}

/**
 * Interface for climb record stored directly in user document
 */
export interface ClimbRecord {
  routeId: string;
  gymId: string;
  date: number; // Timestamp
  performance: ClimbPerformance;
  grade?: number;
  style?: string;
  notes?: string;
  hangs?: number;
  attempt?: number;
  reachedTop?: boolean;
  
  // Additional route details copied to preserve history
  color?: string;
  rope?: string;
  setterId?: string;
  setterName?: string;
  routeNote?: string;
  routeComments?: string;
  routeDate?: number;
  status?: string;
  picture?: string;
  location?: string;
  points?: number;
}

/**
 * Climber model representing a user profile with climbing preferences
 */
@JsonObject()
export class Climber {
  @JsonProperty()
  firebaseId: string;

  @JsonProperty()
  language?: string;

  @JsonProperty()
  boulderGradeSystem?: string;

  @JsonProperty()
  ropeGradeSystem?: string;

  @JsonProperty()
  username?: string;

  @JsonProperty()
  imageUrl?: string;

  @JsonProperty()
  bio?: string;

  @JsonProperty()
  name?: string;

  @JsonProperty()
  age?: number;

  @JsonProperty()
  gender?: string;

  @JsonProperty()
  role?: string;

  @JsonProperty()
  features?: boolean[];

  @JsonProperty()
  isBlocked?: boolean;

  @JsonProperty()
  bananas?: number;

  @JsonProperty()
  lastLogin?: number;

  @JsonProperty()
  subscribed?: string;

  // New property for gym memberships (replaces gyms array)
  @JsonProperty()
  gymMemberships?: GymMembership;

  // Keeping the old gyms array for backward compatibility
  @JsonProperty()
  gyms?: string[];

  @JsonProperty()
  climbs?: ClimbRecord[];

  // For backward compatibility - these will be deprecated
  @JsonProperty({ name: '_climbs' })
  private _legacyClimbs?: Climb[];

  // Not serialized, calculated field
  points?: number;

  constructor(
    firebaseId: string,
    options: {
      language?: string;
      boulderGradeSystem?: string;
      ropeGradeSystem?: string;
      username?: string;
      imageUrl?: string;
      bio?: string;
      name?: string;
      age?: number;
      gender?: string;
      role?: string;
      features?: boolean[];
      isBlocked?: boolean;
      bananas?: number;
      lastLogin?: number;
      subscribed?: string;
      gyms?: string[]; // Legacy property
      gymMemberships?: GymMembership;
      climbs?: ClimbRecord[];
      _legacyClimbs?: Climb[];
      points?: number;
    } = {}
  ) {
    this.firebaseId = firebaseId;
    this.language = options.language || undefined;
    this.boulderGradeSystem = options.boulderGradeSystem || undefined;
    this.ropeGradeSystem = options.ropeGradeSystem || undefined;
    this.username = options.username || undefined;
    this.imageUrl = options.imageUrl || undefined;
    this.bio = options.bio || undefined;
    this.name = options.name || undefined;
    this.age = options.age || undefined;
    this.gender = options.gender || undefined;
    this.role = options.role || 'user';
    this.features = options.features || [];
    this.isBlocked = options.isBlocked === undefined ? false : options.isBlocked;
    this.bananas = options.bananas || 0;
    this.lastLogin = options.lastLogin || Date.now();
    this.subscribed = options.subscribed || undefined;
    
    // Handle legacy gyms array to new gymMemberships format
    if (options.gymMemberships) {
      this.gymMemberships = options.gymMemberships;
    } else if (options.gyms && options.gyms.length > 0) {
      // Convert legacy gyms array to gymMemberships object
      this.gymMemberships = {};
      for (const gymId of options.gyms) {
        this.gymMemberships[gymId] = 'climber'; // Default role
      }
    } else {
      this.gymMemberships = {};
    }
    
    // Also keep the original gyms array for backward compatibility
    this.gyms = options.gyms || [];
    
    this.climbs = options.climbs || [];
    this._legacyClimbs = options._legacyClimbs || [];
    this.points = options.points;
  }

  // Method to convert to Firestore data
  toFirestore() {
    const data: Record<string, any> = {
      firebaseId: this.firebaseId,
      language: this.language || null,
      boulderGradeSystem: this.boulderGradeSystem || null,
      ropeGradeSystem: this.ropeGradeSystem || null,
      username: this.username || null,
      imageUrl: this.imageUrl || null,
      bio: this.bio || null,
      name: this.name || null,
      age: this.age || null,
      gender: this.gender || null,
      role: this.role || 'user',
      features: this.features || [],
      isBlocked: this.isBlocked === undefined ? false : this.isBlocked,
      bananas: this.bananas || 0,
      lastLogin: this.lastLogin || Date.now(),
      subscribed: this.subscribed || null,
      gymMemberships: this.gymMemberships || {},
      gyms: this.getGymIds(), // Convert from gymMemberships for backward compatibility
      climbs: this.climbs || []
    };

    // For backward compatibility
    if (this._legacyClimbs && this._legacyClimbs.length > 0) {
      data._climbs = this._legacyClimbs.map(climb => climb.toFirestore());
    }

    return data;
  }

  // Static method to create Climber from Firestore data
  static fromFirestore(data: any): Climber {
    let legacyClimbs: Climb[] | undefined;
    
    // Handle legacy climbs format if present
    if (Array.isArray(data._climbs)) {
      legacyClimbs = data._climbs.map((climbData: any) => Climb.fromFirestore(climbData));
    }

    return new Climber(data.firebaseId, {
      language: data.language,
      boulderGradeSystem: data.boulderGradeSystem,
      ropeGradeSystem: data.ropeGradeSystem,
      username: data.username,
      imageUrl: data.imageUrl,
      bio: data.bio,
      name: data.name,
      age: data.age,
      gender: data.gender,
      role: data.role,
      features: data.features,
      isBlocked: data.isBlocked,
      bananas: data.bananas,
      lastLogin: data.lastLogin,
      subscribed: data.subscribed,
      gyms: data.gyms, // For backward compatibility
      gymMemberships: data.gymMemberships,
      climbs: data.climbs,
      _legacyClimbs: legacyClimbs
    });
  }

  // Method to add a climb record 
  addClimbRecord(climbRecord: ClimbRecord): void {
    if (!this.climbs) {
      this.climbs = [];
    }
    // Add at the beginning for most recent first
    this.climbs.unshift(climbRecord);
  }

  // Method to get total number of climbs (including only those in the user document)
  getTotalClimbs(): number {
    return this.climbs?.length || 0;
  }

  // Method to calculate points based on climbs
  // Note: This now only counts climbs stored in the user document
  calculatePoints(): number {
    if (!this.climbs || this.climbs.length === 0) {
      return 0;
    }

    // Sum up points from climbs with points info
    return this.climbs.reduce((total, climb) => {
      // First check if the climb has stored points from the route
      if (climb.points !== undefined && climb.points > 0) {
        return total + climb.points;
      }
      // Fallback to using grade as points
      return total + (climb.grade || 0);
    }, 0);
  }

  // Method to get success rate from the recent climbs
  getSuccessRate(): number {
    if (!this.climbs || this.climbs.length === 0) {
      return 0;
    }
    
    const successfulClimbs = this.climbs.filter(
      climb => climb.performance === ClimbPerformance.TOPPED || 
               climb.performance === ClimbPerformance.FLASH ||
               climb.performance === ClimbPerformance.ON_SIGHT ||
               climb.performance === ClimbPerformance.REDPOINT
    );

    return (successfulClimbs.length / this.climbs.length) * 100;
  }

  // Check if climber is a member of a specific gym
  isMemberOfGym(gymId: string): boolean {
    return this.gymMemberships ? !!this.gymMemberships[gymId] : false;
  }

  // Get role in a specific gym
  getRoleInGym(gymId: string): string | null {
    return this.gymMemberships && this.gymMemberships[gymId] ? this.gymMemberships[gymId] : null;
  }

  // Add or update gym membership
  updateGymMembership(gymId: string, role: string = 'climber'): void {
    if (!this.gymMemberships) {
      this.gymMemberships = {};
    }
    this.gymMemberships[gymId] = role;
    
    // Update the gyms array for backward compatibility
    this.updateGymsArray();
  }

  // Remove gym membership
  removeGymMembership(gymId: string): void {
    if (this.gymMemberships && this.gymMemberships[gymId]) {
      delete this.gymMemberships[gymId];
      
      // Update the gyms array for backward compatibility
      this.updateGymsArray();
    }
  }

  // Get all gym IDs where user is a member
  getGymIds(): string[] {
    return this.gymMemberships ? Object.keys(this.gymMemberships) : [];
  }
  
  // Update the gyms array based on gymMemberships for backward compatibility
  private updateGymsArray(): void {
    this.gyms = this.getGymIds();
  }
}